import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="space-y-3">
        <h2 className="text-xl font-semibold text-snapfy-text-primary">{title}</h2>
        <div className="text-snapfy-text-secondary space-y-2">{children}</div>
    </section>
);

const SectionDivider: React.FC = () => (
    <div className="py-4">
        <div className="w-16 border-t border-snapfy-border"></div>
    </div>
);

const PrivacyPage: React.FC = () => {
    return (
        <div className="max-w-3xl mx-auto py-8 px-4 text-snapfy-text-secondary">
            <header className="mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-snapfy-text-primary mb-2">Política de Privacidade</h1>
                <p className="text-sm text-snapfy-text-secondary">Última atualização: 25 de junho de 2025</p>
            </header>

            <div className="space-y-8">
                <p className="text-snapfy-text-secondary">
                    A SNAPFY.AI LTDA., inscrita no CNPJ sob o nº 57.189.141/0001-11, valoriza a sua privacidade e está comprometida com a transparência no tratamento de dados pessoais dos usuários que acessam e utilizam nossa plataforma, disponível em www.snapfy.ai.
                </p>
                <p className="text-snapfy-text-secondary">
                    Esta Política de Privacidade foi elaborada de acordo com a Lei Geral de Proteção de Dados Pessoais (LGPD) e descreve como coletamos, utilizamos, armazenamos e protegemos os seus dados.
                </p>

                <SectionDivider />

                <Section title="1. Quais dados coletamos">
                    <p>Ao utilizar a Snapfy.ai, podemos coletar os seguintes dados pessoais:</p>
                    <h3 className="font-semibold text-snapfy-text-secondary pt-2">Dados fornecidos pelo usuário:</h3>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Nome completo</li>
                        <li>E-mail</li>
                        <li>Senha (criptografada)</li>
                        <li>CPF ou CNPJ (em casos de faturamento)</li>
                        <li>Dados de cartão de crédito (armazenados de forma segura por meio de intermediadores de pagamento)</li>
                        <li>Informações inseridas voluntariamente em campos de perfil, campanhas ou prompts</li>
                    </ul>
                    <h3 className="font-semibold text-snapfy-text-secondary pt-2">Dados coletados automaticamente:</h3>
                     <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Endereço IP</li>
                        <li>Navegador e tipo de dispositivo</li>
                        <li>Cookies e identificadores de sessão</li>
                        <li>Páginas visitadas e ações realizadas dentro da plataforma</li>
                    </ul>
                </Section>

                <SectionDivider />

                <Section title="2. Como utilizamos os dados">
                    <p>Seus dados são utilizados para as seguintes finalidades:</p>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Criar e gerenciar sua conta na plataforma</li>
                        <li>Processar pagamentos e faturas</li>
                        <li>Gerar imagens a partir de arquivos enviados</li>
                        <li>Oferecer suporte técnico e atendimento ao cliente</li>
                        <li>Enviar comunicações transacionais e promocionais (quando autorizado)</li>
                        <li>Cumprir obrigações legais e regulatórias</li>
                    </ul>
                </Section>
                
                <SectionDivider />

                <Section title="3. Compartilhamento de dados">
                    <p>A Snapfy.ai não vende seus dados pessoais. Compartilhamos informações apenas com:</p>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Prestadores de serviço essenciais (ex: provedores de pagamento, hospedagem, e-mail marketing)</li>
                        <li>Autoridades legais, quando houver obrigação judicial ou regulatória</li>
                    </ul>
                     <p>Todos os parceiros contratados seguem rigorosos padrões de segurança e confidencialidade.</p>
                </Section>
                
                <SectionDivider />

                <Section title="4. Armazenamento e segurança">
                    <p>Seus dados são armazenados em servidores seguros, com medidas técnicas e administrativas para proteger contra acessos não autorizados, vazamentos ou qualquer forma de tratamento indevido.</p>
                    <p>Utilizamos criptografia, autenticação por senha e controle de acesso para proteger suas informações.</p>
                </Section>
                
                <SectionDivider />

                <Section title="5. Cookies">
                    <p>Utilizamos cookies para:</p>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Garantir o funcionamento da plataforma</li>
                        <li>Analisar métricas de uso e performance</li>
                        <li>Personalizar a experiência do usuário</li>
                    </ul>
                    <p>Você pode configurar seu navegador para recusar ou excluir cookies, mas isso pode afetar a funcionalidade da plataforma.</p>
                </Section>
                
                <SectionDivider />

                <Section title="6. Direitos do titular (você)">
                     <p>De acordo com a LGPD, você tem o direito de:</p>
                    <ul className="list-disc list-outside ml-5 space-y-2">
                        <li>Confirmar a existência de tratamento dos seus dados</li>
                        <li>Acessar, corrigir ou excluir seus dados pessoais</li>
                        <li>Solicitar a portabilidade dos dados</li>
                        <li>Revogar o consentimento ou solicitar a anonimização</li>
                        <li>Obter informações sobre compartilhamento com terceiros</li>
                    </ul>
                    <p>Para exercer qualquer um desses direitos, envie um e-mail para: <a href="mailto:contato@snapfy.ai" className="text-snapfy-accent-soft hover:underline">contato@snapfy.ai</a></p>
                </Section>
                
                <SectionDivider />

                <Section title="7. Retenção de dados">
                    <p>Os dados são mantidos enquanto sua conta estiver ativa ou enquanto forem necessários para cumprir obrigações legais e regulatórias. Você pode solicitar a exclusão definitiva dos seus dados a qualquer momento.</p>
                </Section>
                
                <SectionDivider />
                
                <Section title="8. Alterações nesta política">
                     <p>A Snapfy.ai poderá atualizar esta Política de Privacidade periodicamente. Você será notificado por e-mail ou dentro da plataforma sempre que houver mudanças relevantes.</p>
                </Section>

                <SectionDivider />

                <Section title="9. Contato">
                    <p>Em caso de dúvidas ou solicitações sobre esta política, entre em contato:</p>
                    <p>📧 <a href="mailto:contato@snapfy.ai" className="text-snapfy-accent-soft hover:underline">contato@snapfy.ai</a></p>
                </Section>
            </div>
        </div>
    );
};

export default PrivacyPage;